import React, { useCallback, useEffect, useMemo, useRef, useState, VFC } from 'react'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import FlexingContainer from '@sport1/news-styleguide/FlexingContainer'
import FontelloIcon from '@sport1/news-styleguide/FontelloIcon'
import Touchable from '@sport1/news-styleguide/Touchable'
import { PixelType } from '@sport1/news-styleguide/Types'
import { callPianoApi } from '@/components/ThirdParty/Piano/util'
import { useTracking } from '@/utils/tracking/TrackingProvider'

type AccountOverlayProps = {
    isMobile?: boolean
    onPressCallback?: () => void
    isLoggedInCallback?: React.Dispatch<React.SetStateAction<boolean>>
}

const AccountOverlay: VFC<AccountOverlayProps> = ({ isMobile = false, onPressCallback }) => {
    const ref = useRef(null)
    const [displayWidth, setDisplayWidth] = useState(isMobile ? 320 : 800)
    const displayHeight = useMemo(() => (isMobile ? 400 : 600), [isMobile])
    const { trackInteraction } = useTracking()

    const onMessage = useCallback(
        (event: MessageEvent) => {
            if (event.data?.type === 'tracking') {
                trackInteraction({
                    devices: 'all',
                    interaction_category: 'navigation',
                    interaction_action: 'click',
                    interaction_label: `piano-iframe-navigation-${event.data.message}`,
                    platforms: isMobile ? 'mobile' : 'desktop',
                })
            }
        },
        [isMobile, trackInteraction]
    )

    useEffect(() => {
        if (isMobile) setDisplayWidth(Math.round((window.innerWidth / 100) * 90))

        callPianoApi(() => {
            window.tp?.push([
                'init',
                () => {
                    window?.tp?.myaccount?.show({
                        displayMode: 'inline',
                        containerSelector: '#display_account',
                    })
                },
            ])
        })

        window.addEventListener('message', onMessage)
        return () => {
            window.removeEventListener('message', onMessage)
        }
    }, [ref, setDisplayWidth, isMobile, trackInteraction, onMessage])

    return (
        <NonFlexingContainer
            width={displayWidth}
            position="fixed"
            top={`calc(35% - ${displayHeight / 2}px)` as PixelType}
            left={`calc(50% - ${displayWidth / 2}px)` as PixelType}
            zIndex={2147483647}
            borderRadius="radius-m"
            paddingX="spacing-6"
            paddingY="spacing-4"
            elevation={4}
            backgroundColor="WHITE"
            innerRef={ref}
            testID="piano-account-overlay"
        >
            <FlexingContainer alignRight>
                <Touchable onPress={onPressCallback}>
                    <FontelloIcon name="close" fontSize="FS16" fontWeight="bold" />
                </Touchable>
            </FlexingContainer>
            <FlexingContainer id="display_account" />
        </NonFlexingContainer>
    )
}

export default AccountOverlay
